import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useClientAdmissions } from "utils/studentService";
import axios from "axios";
import DeleteModal from "components/Modals/DeleteModal";
import { fetchOtherEntryDeletePreview } from "./otherEntryApi";
import EditModuleChargesModal from "components/Modals/EditModuleChargesModal";
import ApplicationWithdrawPanel from "views/admin/Admissions/AddAdmissions/ApplicationWithdrawPanel";
import WithdrawalSettlementDialog from "./withdrawal/WithdrawalSettlementDialog";
import { API_URL, studentInitialValue } from "const/constants";
import { P } from "const/permissions";
import useKothar from "context/useKothar";
import { hasPermissionKey } from "utils/permissionUtils";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { advanceInstallmentBaselines, withInstallmentBaseline } from "./installmentBaseline";
import {
  ADMISSION_ALREADY_EXISTS_MESSAGE,
  ADMISSION_CREATE_FIRST_TOAST,
  ADMISSION_MODULE_LOCKED_MESSAGE,
  ADMISSION_TERM_SCHEDULE_REQUIRED_MESSAGE,
  ADMISSION_WITHDRAWN_STATUS,
  admissionHasTermSchedule,
  admissionRowHasProtectedLinks,
  findDuplicateAdmissionRows,
  buildAdmissionInstallmentsFromTermSchedule,
  buildAdmissionValuesFromStudent,
  calculateRowValues,
  calculateTotals,
  mergeAdmissionInstallmentsWithTerms,
  projectAccountDetails,
  sumAdmissionInstallmentTotals,
  createNewAccountDetail,
  getAdmissionTermSchedule,
  installmentsToTermSchedule,
  countPendingVerificationsForAdmission,
  isAdmissionRowCompleted,
  parseAdmissionApplicationData,
  reconcileAdmissionRowWithStudent,
  shouldShowAdmissionInAccounts,
  validateAdmissionAccountDetails,
  resolveAccountClientData,
  isNavClientStub,
  accountDataMatchesNavigation,
} from "./accountUtils";
import {
  usePendingPaymentVerifications,
  usePendingTermVerifications,
} from "./verification/verificationTaskApi";
import { usePendingModulePaymentVerifications } from "./verification/modulePaymentVerificationApi";
import AccountVerificationModal from "./verification/AccountVerificationModal";
import EyeModal from "./EyeModal";
import NotesModal from "./NotesModal";
import GenerateInvoice from "./GenerateInvoice";
import SendToProviderModal from "./SendToProviderModal";
import GenericPaymentsModal from "./GenericPaymentsModal";
import LinkedModulePaymentsModal from "./LinkedModulePaymentsModal";
import { syncCadPayments } from "./accountsApi";
import {
  enrichPaymentsFromNotes,
  findNoteForPayment,
  findPaymentForNote,
  isPaymentNote,
  isSameNote,
} from "./modulePaymentNoteUtils";
import {
  hydrateModulePaymentsForRow,
  isLinkedModuleRow,
  isOtherEntryRow,
  normalizeModulePayments,
  persistAccountDetailNote,
  prefetchModulePaymentsMap,
} from "./modulePaymentsRowUtils";
import { MODULE_CLIENT_CONFIGS } from "../shared/moduleClients/moduleClientConfigs";
import { getThemedTabsSx } from "theme/tabsTheme";
import AccountEditorHeader from "./accountEditor/AccountEditorHeader";
import AccountFinancialSummary from "./accountEditor/AccountFinancialSummary";
import AccountOverviewPanel from "./accountEditor/AccountOverviewPanel";
import AccountAdmissionPanel from "./accountEditor/AccountAdmissionPanel";
import AccountAdmissionDetailPage from "./accountEditor/AccountAdmissionDetailPage";
import AccountLinkedModulePanel from "./accountEditor/AccountLinkedModulePanel";
import AccountOthersPanel from "./accountEditor/AccountOthersPanel";
import InsuranceCommissionModal from "./InsuranceCommissionModal";
import { setCommissionCompletedRequest } from "./insuranceCommissionApi";

const LINKED_MODULE_TYPE_TO_CONFIG = {
  VISA: MODULE_CLIENT_CONFIGS.visa,
  INSURANCE: MODULE_CLIENT_CONFIGS.insurance,
  SKILL_ASSESSMENT: MODULE_CLIENT_CONFIGS.skill,
  RPL_CLIENT: MODULE_CLIENT_CONFIGS.rpl,
};

const ACCOUNT_TABS = [
  { id: "overview", label: "Overview", moduleValue: undefined },
  { id: "admission", label: "Admission", moduleValue: "Admission" },
  { id: "visa", label: "Visa", moduleValue: "Visa" },
  { id: "insurance", label: "Insurance", moduleValue: "Insurance" },
  { id: "skill", label: "Skill Assessment", moduleValue: "Skill Assessment" },
  { id: "rpl", label: "RPL", moduleValue: "RPL" },
  { id: "others", label: "Others", moduleValue: null },
];
const DEFAULT_ACCOUNT_TAB_ID = "overview";
const MODULE_VALUE_TO_TAB_ID = ACCOUNT_TABS.reduce((acc, tab) => {
  if (tab.moduleValue) acc[tab.moduleValue] = tab.id;
  return acc;
}, {});
const tabIdForModule = (moduleValue) => MODULE_VALUE_TO_TAB_ID[moduleValue] || "others";

/**
 * The legacy module-payment sync is refused for a row the server has linked to a module, whose
 * payments live on the verification ledger instead. An editor loaded before that link happened
 * still holds the unlinked copy, and failing the whole save over a list that is not ours to write
 * left the account unsaved with no way forward.
 */
const isLinkedRowSyncRejection = (err) => {
  const message =
    err?.response?.data?.errorMessage || err?.response?.data?.message || err?.message || "";
  return /payment-transactions/i.test(String(message));
};

const DROPDOWN_LABEL_TO_MODULE_CONFIG = {
  Visa: MODULE_CLIENT_CONFIGS.visa,
  Insurance: MODULE_CLIENT_CONFIGS.insurance,
  RPL: MODULE_CLIENT_CONFIGS.rpl,
  "Skill Assessment": MODULE_CLIENT_CONFIGS.skill,
};

const SYNCED_MODULE_DELETE_BLOCKED_MESSAGE =
  "This entry is linked to a module record. Delete it from that module's own page instead — deleting it here would just have it resynced.";

const AddAccounts = ({ color = "light" }) => {
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [admissionSelectDialog, setAdmissionSelectDialog] = useState({
    open: false,
    rowUuid: null,
    options: [],
    missingScheduleCount: 0,
  });
  const [openNotesModal, setOpenNotesModal] = useState({
    state: false,
    id: null,
    data: null,
  });
  const [openEyeModal, setOpenEyeModal] = useState({
    state: false,
    uuid: null,
    id: null,
  });
  const [studentValues, setStudentValues] = useState(studentInitialValue);
  const [data, setData] = useState({
    clientData: null,
    caseOfficer: "",
    referral: "",
  });
  const [clientData, setClientData] = useState(null);
  const [
    {
      accountData,
      accountDetails,
      accountDataLoading,
      usersList,
      courseList,
      uniData,
      providerList,
    },
    {
      refetchAccountList,
      getIndividualAccountData,
      accountFetchSeqRef,
      resetAccountViewForNavigation,
      setAccountData,
      setAccountDetails,
      setAccountDataLoading,
      refetchCourseList,
      refetchUniData,
      refetchProvider,
    },
  ] = useKothar();
  // Scoped to this account's client only — not the org-wide studentList context query,
  // which loads every admission in the org just to filter down to this one client's rows.
  const {
    data: studentList = [],
    refetch: refetchStudent,
    isLoading: studentAdmissionsLoading,
  } = useClientAdmissions(data?.clientData?.id);
  const [isGenerateInvoiceOpen, setIsGenerateInvoiceOpen] = useState({ id: null, uuid: null });
  const [sendToProviderOpen, setSendToProviderOpen] = useState(false);
  const [modulePaymentsMap, setModulePaymentsMap] = useState({});
  const [paymentsModal, setPaymentsModal] = useState({ open: false, row: null });
  const [chargesModal, setChargesModal] = useState({ open: false, row: null });
  const [commissionToggleBusyId, setCommissionToggleBusyId] = useState(null);
  const [insuranceCommissionModal, setInsuranceCommissionModal] = useState({
    open: false,
    row: null,
  });
  const [isSaving, setIsSaving] = useState(false);
  const modulePaymentsMapRef = useRef(modulePaymentsMap);
  const lastSyncedModulePaymentsRef = useRef({});
  const isManualSavingRef = useRef(false);
  const skipNextMutationErrorToastRef = useRef(false);
  const pendingSuccessToastRef = useRef(null);
  // What the rows looked like before an edit that was applied before the server accepted it. A
  // rejected save (a paid referral amount, say) must not leave the screen showing the new figure.
  const optimisticRollbackRef = useRef(null);

  const modulePaymentsPayloadSignature = useCallback(
    (payments) =>
      JSON.stringify(
        (payments || []).map((p) => ({
          uuid: p.id,
          amount: p.amount,
          date: p.date,
          reference: p.reference,
          createdAt: p.createdAt,
        }))
      ),
    []
  );

  const syncAllModulePayments = useCallback(
    async (details, paymentsMap = modulePaymentsMapRef.current) => {
      // Linked module rows (auto-synced from a real Visa/Insurance/RPL/Skill Assessment record) manage
      // payments via /payment-transactions + verification — the generic /module-payments/sync write the
      // backend serves this loop is rejected for them (400 "Linked module rows use payment-transactions").
      const rows = (details || []).filter(
        (r) => r.id && r.module !== "Admission" && !isLinkedModuleRow(r)
      );
      for (const row of rows) {
        if (!Object.prototype.hasOwnProperty.call(paymentsMap, row.id)) continue;
        const payments = paymentsMap[row.id] || [];
        const signature = modulePaymentsPayloadSignature(payments);
        if (lastSyncedModulePaymentsRef.current[row.id] === signature) continue;
        try {
          await syncCadPayments(
            row.id,
            payments.map((p) => ({
              uuid: p.id,
              amount: p.amount,
              date: p.date,
              reference: p.reference,
              createdAt: p.createdAt,
            }))
          );
        } catch (err) {
          if (!isLinkedRowSyncRejection(err)) throw err;
        }
        lastSyncedModulePaymentsRef.current[row.id] = signature;
      }
    },
    [modulePaymentsPayloadSignature]
  );

  const openModulePaymentsModal = useCallback(
    async (row) => {
      await hydrateModulePaymentsForRow(row, setModulePaymentsMap, setAccountDetails);
      setPaymentsModal({ open: true, row });
    },
    [setAccountDetails]
  );
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const [activeTabId, setActiveTabIdState] = useState(() => {
    const initial = new URLSearchParams(window.location.search).get("section");
    return ACCOUNT_TABS.some((tab) => tab.id === initial) ? initial : DEFAULT_ACCOUNT_TAB_ID;
  });
  const [activeEntryId, setActiveEntryIdState] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get("section");
    const entry = params.get("entry");
    if (section === "admission" && entry) return entry;
    return null;
  });
  const setActiveEntryId = useCallback((uuid) => {
    const next = uuid || null;
    setActiveEntryIdState(next);
    const params = new URLSearchParams(window.location.search);
    if (next) params.set("entry", next);
    else params.delete("entry");
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}?${params.toString()}${window.location.hash}`
    );
  }, []);
  const setActiveTabId = useCallback((tabId) => {
    setActiveTabIdState(tabId);
    const params = new URLSearchParams(window.location.search);
    params.set("section", tabId);
    if (tabId !== "admission") {
      params.delete("entry");
      setActiveEntryIdState(null);
    }
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}?${params.toString()}${window.location.hash}`
    );
  }, []);
  const activeTab = useMemo(
    () => ACCOUNT_TABS.find((tab) => tab.id === activeTabId) || ACCOUNT_TABS[0],
    [activeTabId]
  );
  const [verificationFocusUuid, setVerificationFocusUuid] = useState(null);
  const [autoOpenVerification, setAutoOpenVerification] = useState(false);
  const [clientVerificationModalOpen, setClientVerificationModalOpen] = useState(false);
  const [moduleVerificationScope, setModuleVerificationScope] = useState(null);
  const [termNotesFocus, setTermNotesFocus] = useState(null);
  const [settlementDialog, setSettlementDialog] = useState(null);
  const accountFocusHandledRef = useRef(false);

  // --- Auto-save plumbing (no state-handling changes; layered on top) ---
  const AUTOSAVE_DELAY_MS = 3500;
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // idle | dirty | saving | saved | error
  const autoSaveTimerRef = useRef(null);
  const savedClearTimerRef = useRef(null);
  const isAutoSavingRef = useRef(false);
  const accountDetailsRef = useRef(accountDetails);
  const dataRef = useRef(data);
  const accountDataRef = useRef(accountData);
  const getIndividualAccountDataRef = useRef(getIndividualAccountData);
  getIndividualAccountDataRef.current = getIndividualAccountData;
  const resetAccountViewRef = useRef(resetAccountViewForNavigation);
  resetAccountViewRef.current = resetAccountViewForNavigation;
  const accountNavigationRef = useRef(0);
  const reconciledFromStudentRef = useRef(null);
  // Stable wrapper so handlers below can reference markDirty without TDZ.
  // The actual implementation is wired up after useMutation (see markDirtyRef.current = ...).
  const markDirtyRef = useRef(() => {});
  const markDirty = useCallback(() => markDirtyRef.current?.(), []);
  const autoSaveSnapshotRef = useRef(null);

  // Post notes that were added locally before the entry had a server-assigned id.
  // Called via the onFetched callback of getIndividualAccountData, which fires
  // before setAccountDetails overwrites local state, so snapshotDetails still
  // holds the pending notes at call time.
  const postPendingLocalNotes = useCallback((snapshotDetails, savedAccountDetails) => {
    const pendingByUuid = {};
    (snapshotDetails || []).forEach((item) => {
      const outerPending = (item.notes || []).filter((n) => !n.id);
      if (outerPending.length)
        pendingByUuid[item.uuid] = { notes: outerPending, parentType: "ACCOUNT_DETAIL" };
      (item.admissionDetails || []).forEach((sub) => {
        const pending = (sub.notes || []).filter((n) => !n.id);
        if (pending.length)
          pendingByUuid[sub.uuid] = { notes: pending, parentType: "ADMISSION_DETAIL" };
      });
    });
    if (!Object.keys(pendingByUuid).length) return Promise.resolve(false);
    const requests = [];
    (savedAccountDetails || []).forEach((item) => {
      const outerEntry = pendingByUuid[item.uuid];
      if (outerEntry?.notes?.length && item.id) {
        outerEntry.notes.forEach((note) => {
          if (!note.text) return;
          const form = new FormData();
          form.append("parentId", item.id);
          form.append("parentType", "ACCOUNT_DETAIL");
          form.append("noteText", note.text);
          requests.push(
            axios
              .post(`${API_URL}/notes`, form, {
                headers: { "Content-Type": "multipart/form-data" },
              })
              .catch((err) => console.error("Failed to save pending outer note", err))
          );
        });
      }
      (item.admissionDetails || []).forEach((sub) => {
        const entry = pendingByUuid[sub.uuid];
        if (!entry?.notes?.length || !sub.id) return;
        entry.notes.forEach((note) => {
          if (!note.text) return;
          const form = new FormData();
          form.append("parentId", sub.id);
          form.append("parentType", "ADMISSION_DETAIL");
          form.append("noteText", note.text);
          requests.push(
            axios
              .post(`${API_URL}/notes`, form, {
                headers: { "Content-Type": "multipart/form-data" },
              })
              .catch((err) => console.error("Failed to save pending sub note", err))
          );
        });
      });
    });
    return Promise.all(requests).then(() => true);
  }, []);

  const mapAccountDetailForPayload = useCallback(
    (item) => {
      let sourceRow = item;
      if (item.module === "Admission" && item.admissionValues?.studentDbId) {
        const student = (studentList || []).find((s) => s?.id === item.admissionValues.studentDbId);
        if (student && courseList?.length && uniData?.length) {
          sourceRow = reconcileAdmissionRowWithStudent(item, {
            student,
            courseList,
            uniData,
          });
        }
      }

      let admissionValues = sourceRow.admissionValues;
      if (sourceRow.module === "Admission" && sourceRow.admissionDetails?.length) {
        const termSchedule = installmentsToTermSchedule(
          sourceRow.admissionDetails,
          sourceRow.admissionValues?.termSchedule || []
        );
        admissionValues = { ...sourceRow.admissionValues, termSchedule };
      }
      return {
        id: sourceRow.id,
        uuid: sourceRow.uuid,
        module: sourceRow.module,
        admissionValues: sourceRow.module === "Admission" ? admissionValues : null,
        admissionDetails: sourceRow.module === "Admission" ? sourceRow.admissionDetails : [],
        agreedAmount: sourceRow.agreedAmount,
        cost: sourceRow.cost,
        paidAmount: sourceRow.paidAmount,
        dueAmount: sourceRow.dueAmount,
        dueDate: sourceRow.dueDate,
        profitLoss: sourceRow.profitLoss,
        referral: sourceRow?.referral,
        discount: sourceRow.discount,
        commission: sourceRow.commission,
        bonus: sourceRow.bonus,
        agreed: sourceRow.agreed,
        isCommissionClaimed: sourceRow.isCommissionClaimed ?? false,
        isBonusClaimed: sourceRow.isBonusClaimed ?? false,
        ...(sourceRow.module === "Insurance"
          ? {
              commissionMonths: sourceRow.commissionMonths || [],
              commissionCompleted: sourceRow.commissionCompleted ?? false,
            }
          : {}),
      };
    },
    [courseList, studentList, uniData]
  );

  useEffect(() => {
    if (!courseList?.length) refetchCourseList?.();
    if (!uniData?.length) refetchUniData?.();
    if (!providerList?.length) refetchProvider?.();
  }, []);

  const latestAdmissionForClient = useCallback(
    (clientId) => {
      const matches = (studentList || []).filter((student) => student?.clientId === clientId);
      return matches[matches.length - 1] || null;
    },
    [studentList]
  );

  const admissionsForClient = useCallback(
    (clientId) => (studentList || []).filter((s) => s?.clientId === clientId),
    [studentList]
  );

  const usedAdmissionDbIds = useMemo(
    () =>
      new Set(
        (accountDetails || [])
          .filter((row) => row.module === "Admission" && row.admissionValues?.studentDbId)
          .map((row) => row.admissionValues.studentDbId)
      ),
    [accountDetails]
  );

  const hydrateAdmissionValues = useCallback(
    (existingValues = {}) => {
      const linkedAdmission = existingValues?.studentDbId
        ? (studentList || []).find((student) => student?.id === existingValues.studentDbId)
        : null;
      return buildAdmissionValuesFromStudent({
        student: linkedAdmission || latestAdmissionForClient(data?.clientData?.id),
        courseList,
        uniData,
        fallback: existingValues || studentInitialValue,
      });
    },
    [courseList, data?.clientData?.id, latestAdmissionForClient, studentList, uniData]
  );

  // Initialize state from context or default

  const hydrateAccountFromApi = useCallback(
    (apiData) => {
      if (!accountDataMatchesNavigation(state?.item, apiData)) return false;
      if (!apiData?.accountDetails?.length) return false;
      const updatedAccountDetails = apiData.accountDetails
        .map((item) => ({
          ...item,
          uuid: item.uuid || (item.id ? `cad-${item.id}` : crypto.randomUUID()),
          module: item.module || "RPL",
          admissionValues: item.admissionValues || studentInitialValue,
          admissionDetails: (item.admissionDetails || []).map((sub) =>
            withInstallmentBaseline({
              ...sub,
              uuid: sub.uuid || (sub.id ? `sub-${sub.id}` : crypto.randomUUID()),
              notes: sub.notes || [],
            })
          ),
          notes: item.notes || [],
          isCommissionClaimed: item.isCommissionClaimed ?? false,
          isBonusClaimed: item.isBonusClaimed ?? false,
          commissionMonths: item.commissionMonths || [],
          commissionCompleted: item.commissionCompleted ?? false,
        }))
        .map(calculateRowValues);
      const resolvedClient = resolveAccountClientData(
        state?.item?.clientData,
        apiData?.clientData,
        null
      );
      setAccountData(apiData);
      setData((prev) => ({
        ...prev,
        ...apiData,
        clientData: resolveAccountClientData(
          state?.item?.clientData,
          apiData?.clientData,
          prev.clientData
        ),
      }));
      if (resolvedClient?.name) {
        setClientData(resolvedClient);
      }
      reconciledFromStudentRef.current = null;
      setAccountDetails(updatedAccountDetails);
      prefetchModulePaymentsMap(updatedAccountDetails, setModulePaymentsMap, setAccountDetails);
      return true;
    },
    [setAccountData, setAccountDetails, setClientData, state?.item]
  );

  // Prefetch module-payment lists for non-admission rows not yet loaded, so the Payments badge count shows on first
  // render — not all load paths run hydrateAccountFromApi (e.g. the clientAccountId path), which would otherwise
  // leave counts at 0 until the payments modal is opened.
  useEffect(() => {
    const pending = (accountDetails || []).filter(
      (r) =>
        r?.id &&
        r.module !== "Admission" &&
        !Object.prototype.hasOwnProperty.call(modulePaymentsMap, r.id)
    );
    if (!pending.length) return;
    prefetchModulePaymentsMap(pending, setModulePaymentsMap, setAccountDetails);
  }, [accountDetails, modulePaymentsMap, setAccountDetails]);

  useLayoutEffect(() => {
    const navId = ++accountNavigationRef.current;
    const clientDataFromNav = state?.item?.clientData ?? null;
    const clientAccountId = state?.item?.clientAccountId ?? null;
    const hasAccountDetails = state?.item?.hasAccountDetails ?? Boolean(clientAccountId);

    const isStaleNavigation = () => navId !== accountNavigationRef.current;

    setVerificationFocusUuid(null);
    setAutoOpenVerification(false);
    if (state?.accountFocus) {
      accountFocusHandledRef.current = false;
    }
    resetAccountViewRef.current();
    setModulePaymentsMap({});
    lastSyncedModulePaymentsRef.current = {};
    reconciledFromStudentRef.current = null;

    setClientData(clientDataFromNav);
    setData({
      clientData: clientDataFromNav,
      caseOfficer: "",
      referral: clientDataFromNav?.referral ?? "",
    });

    const navItem = state?.item;

    const applyClientFromApi = (apiData) => {
      if (isStaleNavigation()) return;
      if (!accountDataMatchesNavigation(navItem, apiData)) return;
      if (apiData?.clientData) {
        setClientData(apiData.clientData);
        setData((prev) => ({ ...prev, clientData: apiData.clientData }));
      } else if (apiData?.clientId) {
        axios
          .get(`${API_URL}/accounts/client/${apiData.clientId}`)
          .then((res) => {
            if (isStaleNavigation()) return;
            const cd = res?.data?.data?.clientData;
            if (clientDataFromNav?.id && cd?.id && String(cd.id) !== String(clientDataFromNav.id)) {
              return;
            }
            if (cd) {
              setClientData(cd);
              setData((prev) => ({ ...prev, clientData: cd }));
            }
          })
          .catch(() => {});
      }
    };

    if (hasAccountDetails && clientAccountId) {
      getIndividualAccountDataRef.current(true, clientAccountId, (apiData) => {
        reconciledFromStudentRef.current = null;
        applyClientFromApi(apiData);
      });
      return;
    }

    if (!clientDataFromNav?.id) {
      setAccountDataLoading(false);
      return;
    }

    const loadExistingByClient = async () => {
      const fetchSeqAtStart = accountFetchSeqRef.current;
      const isStaleFetch = () =>
        isStaleNavigation() || fetchSeqAtStart !== accountFetchSeqRef.current;
      try {
        const res = await axios.get(`${API_URL}/accounts/client/${clientDataFromNav.id}`);
        if (isStaleFetch()) return;
        if (res?.data?.success && res?.data?.data) {
          const apiPayload = res.data.data;
          if (!accountDataMatchesNavigation(navItem, apiPayload)) return;
          const hydrated = hydrateAccountFromApi(apiPayload);
          if (!hydrated && !isStaleFetch()) {
            setAccountData(apiPayload);
            setAccountDetails([createNewAccountDetail()]);
          }
          const profile = resolveAccountClientData(clientDataFromNav, apiPayload?.clientData, null);
          if (profile?.name) {
            setClientData(profile);
            setData((prev) => ({ ...prev, clientData: profile }));
          } else if (apiPayload?.clientId || clientDataFromNav?.id) {
            const cd = apiPayload?.clientData;
            if (cd?.name) {
              setClientData(cd);
              setData((prev) => ({ ...prev, clientData: cd }));
            }
          }
        } else {
          setAccountData(null);
          setAccountDetails([createNewAccountDetail()]);
        }
      } catch {
        if (!isStaleFetch()) {
          setAccountData(null);
          setAccountDetails([createNewAccountDetail()]);
        }
      } finally {
        if (!isStaleFetch()) {
          setAccountDataLoading(false);
        }
      }
    };
    loadExistingByClient();
  }, [
    state?.item?.clientAccountId,
    state?.item?.clientData?.id,
    state?.item?.hasAccountDetails,
    // hydrateAccountFromApi intentionally omitted: its identity changes when
    // state.item.clientData changes (already in deps above), which would
    // cause this effect to re-fire and call resetAccountViewForNavigation()
    // a second time — wiping freshly-saved data.
    setAccountDetails,
    setAccountDataLoading,
    state,
  ]);

  // Sync client profile from loaded account when list row had no embedded clientData
  useEffect(() => {
    if (!accountDataMatchesNavigation(state?.item, accountData)) return;

    const targetAccountId = state?.item?.clientAccountId;
    const expectedClientId = state?.item?.clientData?.id;

    const navClient = state?.item?.clientData;
    const needsClientProfile =
      !data.clientData?.name || isNavClientStub(navClient) || isNavClientStub(data.clientData);

    if (accountData?.clientData?.name && needsClientProfile) {
      setClientData(accountData.clientData);
      setData((prev) => ({ ...prev, clientData: accountData.clientData }));
    } else if (accountData?.clientId && !accountData?.clientData?.name && needsClientProfile) {
      axios
        .get(`${API_URL}/accounts/client/${accountData.clientId}`)
        .then((res) => {
          if (!accountDataMatchesNavigation(state?.item, accountData)) return;
          const cd = res?.data?.data?.clientData;
          if (expectedClientId && cd?.id && String(cd.id) !== String(expectedClientId)) return;
          if (cd) {
            setClientData(cd);
            setData((prev) => ({ ...prev, clientData: cd }));
          }
        })
        .catch(() => {});
    }
  }, [
    accountData?.clientData,
    accountData?.clientId,
    accountData?.id,
    data.clientData,
    state?.item?.clientAccountId,
    state?.item?.clientData?.id,
  ]);

  const linkedAdmissionStudentIds = useMemo(
    () =>
      (accountDetails || [])
        .filter((row) => row.module === "Admission" && row.admissionValues?.studentDbId)
        .map((row) => row.admissionValues.studentDbId)
        .join(","),
    [accountDetails]
  );

  const admissionRowsNeedCourseCollegeHydration = useCallback(
    (rows = accountDetails) =>
      (rows || []).some(
        (row) =>
          row.module === "Admission" &&
          row.admissionValues?.studentDbId &&
          (!String(row.admissionValues?.course ?? "").trim() ||
            !String(row.admissionValues?.university ?? "").trim())
      ),
    [accountDetails]
  );

  useEffect(() => {
    if (!studentList?.length || !linkedAdmissionStudentIds) return;
    if (!courseList?.length || !uniData?.length) return;
    const accountKey = String(
      accountData?.id || state?.item?.clientAccountId || data?.clientData?.id || "draft"
    );
    const reconcileKey = `${accountKey}:${linkedAdmissionStudentIds}:${courseList.length}:${uniData.length}`;
    if (
      reconciledFromStudentRef.current === reconcileKey &&
      !admissionRowsNeedCourseCollegeHydration()
    ) {
      return;
    }
    reconciledFromStudentRef.current = reconcileKey;

    setAccountDetails((prev) =>
      prev.map((row) => {
        if (row.module !== "Admission" || !row.admissionValues?.studentDbId) return row;
        const student = studentList.find((s) => s.id === row.admissionValues.studentDbId);
        if (!student) return row;
        return reconcileAdmissionRowWithStudent(row, { student, courseList, uniData });
      })
    );
  }, [
    accountData?.id,
    admissionRowsNeedCourseCollegeHydration,
    courseList,
    data?.clientData?.id,
    linkedAdmissionStudentIds,
    state?.item?.clientAccountId,
    studentList,
    uniData,
  ]);

  // Handle input changes for main table

  const applyAdmissionToRow = useCallback(
    (rowUuid, student) => {
      const termSchedule = getAdmissionTermSchedule({ student });
      if (termSchedule.length === 0) {
        toast.error(ADMISSION_TERM_SCHEDULE_REQUIRED_MESSAGE);
        return false;
      }

      const admValues = buildAdmissionValuesFromStudent({
        student,
        courseList,
        uniData,
        fallback: studentInitialValue,
      });
      const installments = buildAdmissionInstallmentsFromTermSchedule(
        rowUuid || crypto.randomUUID(),
        termSchedule,
        []
      );
      setAccountDetails((prev) =>
        prev.map((item) =>
          item.uuid === rowUuid
            ? calculateRowValues({
                ...item,
                module: "Admission",
                admissionValues: admValues,
                admissionDetails: installments,
              })
            : item
        )
      );
      markDirty();
      return true;
    },
    [courseList, markDirty, setAccountDetails, uniData]
  );

  const navigateToCreateAdmission = useCallback(() => {
    const clientId = data?.clientData?.id;
    if (!clientId) {
      toast.error("Save the account with a client first.");
      return;
    }
    navigate("/admin/admission/add", {
      state: {
        preselectClientId: clientId,
        preselectClient: data.clientData,
      },
    });
  }, [data?.clientData, navigate]);

  // Opens the "select which admission" dialog for the given row uuid, or bails out with a toast /
  // redirect if there's nothing eligible to select. Returns whether the dialog was actually opened —
  // callers that pre-created a row just to feed this (e.g. the Admission tab's Add Entry) use that to
  // know whether to roll the row back.
  const promptSelectAdmissionForRow = useCallback(
    (rowUuid) => {
      const clientId = data?.clientData?.id;
      const allAdmissions = admissionsForClient(clientId);
      const usedIds = usedAdmissionDbIds;
      const unused = allAdmissions.filter((s) => !usedIds.has(s.id));
      const ready = unused.filter((s) => admissionHasTermSchedule({ student: s }));
      const missingScheduleCount = unused.length - ready.length;

      if (usedIds.size > 0 && unused.length === 0) {
        toast.warning(
          "There is already an account entry for this client. Add a new admission first."
        );
        return false;
      }

      if (unused.length === 0) {
        toast.info(ADMISSION_CREATE_FIRST_TOAST);
        navigateToCreateAdmission();
        return false;
      }

      if (ready.length === 0) {
        toast.error(
          missingScheduleCount === 1
            ? ADMISSION_TERM_SCHEDULE_REQUIRED_MESSAGE
            : `None of the ${unused.length} available admissions have a term schedule. Add terms under Admissions → Offer letter received first.`
        );
        return false;
      }

      if (missingScheduleCount > 0) {
        toast.info(
          `${missingScheduleCount} admission(s) are hidden because they have no term schedule in Offer letter received.`
        );
      }

      setAdmissionSelectDialog({
        open: true,
        rowUuid,
        options: ready,
        missingScheduleCount,
      });
      return true;
    },
    [admissionsForClient, data?.clientData?.id, navigateToCreateAdmission, usedAdmissionDbIds]
  );

  // Add new account detail
  const addAccountDetails = () => {
    setAccountDetails((prev) => [...prev, createNewAccountDetail()]);
    markDirty();
  };

  // Add new account detail (Admission tab); creates a blank row purely to carry the uuid into the
  // admission-picker dialog, then rolls it back if the picker never actually opened (no eligible
  // admissions / client not saved yet) so an empty orphan row doesn't linger in the Others tab.
  const handleAddAdmissionEntry = useCallback(() => {
    const newRow = createNewAccountDetail();
    setAccountDetails((prev) => [...prev, newRow]);
    markDirty();
    const opened = promptSelectAdmissionForRow(newRow.uuid);
    if (!opened) {
      setAccountDetails((prev) => prev.filter((item) => item.uuid !== newRow.uuid));
    }
  }, [markDirty, promptSelectAdmissionForRow, setAccountDetails]);

  // Delete account detail
  const handleDeleteInstallment = (row) => {
    if (admissionRowHasProtectedLinks(row?.original)) {
      toast.error(ADMISSION_MODULE_LOCKED_MESSAGE);
      return;
    }
    if (isLinkedModuleRow(row?.original)) {
      toast.error(SYNCED_MODULE_DELETE_BLOCKED_MESSAGE);
      return;
    }
    const result = window.confirm("Are you sure you want to delete this entry?");
    if (result) {
      setAccountDetails((prev) => prev.filter((item) => item.uuid !== row?.original?.uuid));
      markDirty();
    }
  };

  // Handle notes modal
  const handleToggleNotes = (item) => {
    setOpenNotesModal((prev) => ({
      state: !prev.state,
      id: item.id,
      data: item,
    }));
  };

  const handleClose = () => {
    setOpenNotesModal({ state: false, id: null, data: null });
  };

  // Handle eye modal for admission details
  const handleOpenEyeModal = (rowData) => {
    const values = {
      ...hydrateAdmissionValues(rowData.original.admissionValues),
      module: rowData.original.module,
    };
    setOpenEyeModal({
      state: true,
      uuid: rowData.original.uuid,
      value: values,
      id: rowData.original.id,
    });
    setStudentValues(values);
    setAccountDetails((prev) =>
      prev.map((item) =>
        item.uuid === rowData.original.uuid
          ? {
              ...item,
              admissionValues: values,
            }
          : item
      )
    );
  };

  // API mutation
  const { mutate, mutateAsync, isPending } = useMutation(postData, {
    onSuccess(response, sentPayload) {
      setAccountDetails((prev) => advanceInstallmentBaselines(prev, sentPayload?.accountDetails));
      const wasAutoSave = isAutoSavingRef.current;
      const customToast = pendingSuccessToastRef.current;
      pendingSuccessToastRef.current = null;
      optimisticRollbackRef.current = null;
      isAutoSavingRef.current = false;

      if (!wasAutoSave) {
        isManualSavingRef.current = false;
        setIsSaving(false);
        refetchAccountList();
      }

      if (wasAutoSave) {
        refetchAccountList();
        const accountId = response?.data?.data?.id || accountDataRef.current?.id;
        const isFirstCreation = !accountDataRef.current?.id && response?.data?.data?.id;
        const snapshot = autoSaveSnapshotRef.current || [];
        const hasPendingNotes = snapshot.some(
          (item) =>
            (item.notes || []).some((n) => !n.id) ||
            (item.admissionDetails || []).some((sub) => (sub.notes || []).some((n) => !n.id))
        );
        // The server self-links a named custom row to the Others module as part of this save.
        // Without re-reading, the editor keeps the unlinked copy, and that copy is what sends a
        // payment down the legacy direct-payment path and then fails the next save.
        const hasUnlinkedCustomRow = snapshot.some(
          (item) =>
            typeof item.module === "string" &&
            item.module.trim() &&
            tabIdForModule(item.module) === "others" &&
            !item.linkedModuleType
        );

        if (isFirstCreation || hasPendingNotes || hasUnlinkedCustomRow) {
          // Re-hydrate to get server-assigned IDs, then post pending notes, then re-hydrate again.
          getIndividualAccountData(true, accountId, (saved) => {
            postPendingLocalNotes(snapshot, saved?.accountDetails).then((hadPending) => {
              if (hadPending) getIndividualAccountData(true, accountId);
            });
          });
        }

        if (customToast) toast.success(customToast);
        setAutoSaveStatus("saved");
        if (savedClearTimerRef.current) clearTimeout(savedClearTimerRef.current);
        savedClearTimerRef.current = setTimeout(() => {
          setAutoSaveStatus((prev) => (prev === "saved" ? "idle" : prev));
        }, 3000);
        return;
      }

      toast.success(
        customToast ||
          (state?.item?.hasAccountDetails ? "Data updated successfully" : "Data added successfully")
      );
      const snapshot = accountDetailsRef.current ? [...accountDetailsRef.current] : [];
      const accountId = response?.data?.data?.id;
      getIndividualAccountData(true, accountId, (saved) => {
        postPendingLocalNotes(snapshot, saved?.accountDetails).then((hadPending) => {
          if (hadPending) getIndividualAccountData(true, accountId);
        });
      });
      setOpenEyeModal({ state: false, uuid: null, index: null });
      setAutoSaveStatus("idle");
    },
    onError(err) {
      const wasAutoSave = isAutoSavingRef.current;
      pendingSuccessToastRef.current = null;
      isAutoSavingRef.current = false;
      if (optimisticRollbackRef.current) {
        const rolledBack = optimisticRollbackRef.current;
        optimisticRollbackRef.current = null;
        accountDetailsRef.current = rolledBack;
        setAccountDetails(rolledBack);
      }
      if (skipNextMutationErrorToastRef.current) {
        skipNextMutationErrorToastRef.current = false;
        if (!wasAutoSave) {
          isManualSavingRef.current = false;
          setIsSaving(false);
        }
        return;
      }
      const apiMessage = err?.response?.data?.errorMessage;
      if (wasAutoSave) {
        setAutoSaveStatus("error");
        if (apiMessage) toast.error(apiMessage);
        return;
      }
      isManualSavingRef.current = false;
      setIsSaving(false);
      toast.error(
        apiMessage ||
          (state?.item?.hasAccountDetails || accountDataRef.current?.id
            ? "Error updating data"
            : "Error submitting data")
      );
    },
  });

  // Keep refs in sync with latest state (used by the autosave timer callback)
  useEffect(() => {
    accountDetailsRef.current = accountDetails;
    dataRef.current = data;
    accountDataRef.current = accountData;
    modulePaymentsMapRef.current = modulePaymentsMap;
  });

  useEffect(() => {
    if (!accountData?.id) return;
    setAccountDetails((prev) =>
      prev.map((row) => (row.module === "Admission" ? calculateRowValues(row) : row))
    );
  }, [accountData?.id, setAccountDetails]);

  useEffect(
    () => () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (savedClearTimerRef.current) clearTimeout(savedClearTimerRef.current);
    },
    []
  );

  // Wire the real markDirty implementation. Handlers above reference the
  // stable `markDirty` wrapper (declared near the top of the component) which
  // delegates to this ref — avoids TDZ since `mutate` is only available here.
  markDirtyRef.current = () => {
    // // disable autosave testing
    // return;
    setAutoSaveStatus("dirty");
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      if (isManualSavingRef.current) {
        setAutoSaveStatus("dirty");
        return;
      }
      const ad = accountDetailsRef.current || [];
      const d = dataRef.current || {};
      const accD = accountDataRef.current || {};
      if (!d?.clientData?.id) {
        setAutoSaveStatus("idle");
        return;
      }
      const admissionValidation = validateAdmissionAccountDetails(ad, studentList);
      if (!admissionValidation.valid) {
        setAutoSaveStatus("error");
        return;
      }
      if (findDuplicateAdmissionRows(ad).length > 0) {
        setAutoSaveStatus("error");
        return;
      }
      try {
        await syncAllModulePayments(ad, modulePaymentsMapRef.current);
      } catch {
        setAutoSaveStatus("error");
        return;
      }
      const totals = calculateTotals(ad);
      const referralEmail = usersList?.find(
        (user) => user.name === clientData?.referral || user?.email === clientData?.referral
      )?.email;
      const payload = {
        id: accD?.id,
        uuid: d.uuid,
        clientId: d.clientData?.id,
        caseOfficer: d.caseOfficer,
        referral: referralEmail,
        totalAmount: totals.totalPaid,
        agentCost: totals.totalCost,
        dueAmount: totals.totalDue,
        profitLoss: totals.totalProfitLoss,
        accountDetails: ad.map(mapAccountDetailForPayload),
      };
      autoSaveSnapshotRef.current = [...ad];
      isAutoSavingRef.current = true;
      setAutoSaveStatus("saving");
      mutate(payload);
    }, AUTOSAVE_DELAY_MS);
  };

  function postData(payload) {
    const accountMasterId = accountDataRef.current?.id;
    if (state?.item?.hasAccountDetails || accountMasterId) {
      return axios.put(`${API_URL}/accounts/${accountMasterId || payload.id}`, payload);
    }
    return axios.post(`${API_URL}/accounts`, payload);
  }
  const getReferralEmail = usersList?.find(
    (user) => user.name === clientData?.referral || user?.email === clientData?.referral
  )?.email;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isManualSavingRef.current || isPending) return;
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    isAutoSavingRef.current = false;

    const admissionValidation = validateAdmissionAccountDetails(accountDetails, studentList);
    if (!admissionValidation.valid) {
      toast.error(admissionValidation.message);
      return;
    }
    if (findDuplicateAdmissionRows(accountDetails).length > 0) {
      toast.error(ADMISSION_ALREADY_EXISTS_MESSAGE);
      return;
    }

    isManualSavingRef.current = true;
    setIsSaving(true);

    try {
      await syncAllModulePayments(accountDetails);
    } catch (err) {
      isManualSavingRef.current = false;
      setIsSaving(false);
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to sync module payments before save."
      );
      return;
    }

    const payload = {
      id: accountData?.id,
      uuid: data.uuid,
      clientId: data.clientData?.id,
      caseOfficer: data.caseOfficer,
      referral: getReferralEmail,
      totalAmount: calculateTotals(accountDetails).totalPaid,
      agentCost: calculateTotals(accountDetails).totalCost,
      dueAmount: calculateTotals(accountDetails).totalDue,
      profitLoss: calculateTotals(accountDetails).totalProfitLoss,
      accountDetails: accountDetails.map(mapAccountDetailForPayload),
    };

    skipNextMutationErrorToastRef.current = true;
    try {
      await mutateAsync(payload);
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          (state?.item?.hasAccountDetails || accountDataRef.current?.id
            ? "Failed to save account changes."
            : "Failed to submit account data.")
      );
    } finally {
      isManualSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleSubmitSideDrawer = (e) => {
    e.preventDefault();
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    isAutoSavingRef.current = false;
    const foundRow = accountDetails?.find((item) => item?.uuid === openEyeModal?.uuid);
    if (!foundRow) {
      toast.error("Admission entry was not found.");
      return;
    }
    const linkedStudent = (studentList || []).find((s) => s?.id === studentValues?.studentDbId);
    const termSchedule =
      Array.isArray(studentValues?.termSchedule) && studentValues.termSchedule.length > 0
        ? studentValues.termSchedule
        : getAdmissionTermSchedule({
            student: linkedStudent,
            admissionValues: studentValues,
          });
    if (termSchedule.length === 0) {
      toast.error(ADMISSION_TERM_SCHEDULE_REQUIRED_MESSAGE);
      return;
    }
    const admissionValuesWithSchedule = {
      ...studentValues,
      termSchedule,
    };
    const generatedInstallments = buildAdmissionInstallmentsFromTermSchedule(
      foundRow.uuid,
      termSchedule,
      foundRow.admissionDetails || [],
      studentValues
    );
    const updatedRow = calculateRowValues({
      ...foundRow,
      uuid: foundRow.uuid,
      module: foundRow.module,
      admissionValues: foundRow.module === "Admission" ? admissionValuesWithSchedule : null,
      admissionDetails: foundRow.module === "Admission" ? generatedInstallments : [],
      censusDate: foundRow.censusDate,
    });
    const rowIndex = accountDetails.findIndex((item) => item.uuid === openEyeModal?.uuid);
    const updatedAccountDetails = [
      ...accountDetails.slice(0, rowIndex),
      updatedRow,
      ...accountDetails.slice(rowIndex + 1, accountDetails.length),
    ];
    const updatedTotals = calculateTotals(updatedAccountDetails);

    setAccountDetails(updatedAccountDetails);

    const payload = {
      id: accountData?.id,
      uuid: data.uuid,
      clientId: data.clientData?.id,
      caseOfficer: data.caseOfficer,
      referral: clientData?.referral,
      totalAmount: updatedTotals.totalPaid,
      agentCost: updatedTotals.totalCost,
      dueAmount: updatedTotals.totalDue,
      profitLoss: updatedTotals.totalProfitLoss,
      accountDetails: updatedAccountDetails.map(mapAccountDetailForPayload),
    };
    mutate(payload);
  };

  // A saved custom entry is deleted through the entry delete endpoint — with the same
  // delete-preview as the client Accounts tab — so its payments and cluster are purged rather
  // than left behind by a local row removal.
  const [deleteOtherTarget, setDeleteOtherTarget] = useState({
    state: false,
    id: null,
    rowUuid: null,
  });

  const deleteOtherPreviewQuery = useQuery({
    queryKey: ["other-entry-delete-preview", deleteOtherTarget.id],
    queryFn: () => fetchOtherEntryDeletePreview(deleteOtherTarget.id),
    enabled: Boolean(deleteOtherTarget.state && deleteOtherTarget.id),
    refetchOnWindowFocus: false,
  });

  const closeDeleteOtherModal = () =>
    setDeleteOtherTarget({ state: false, id: null, rowUuid: null });

  const handleDeleteOtherEntry = () => {
    axios
      .delete(`${API_URL}/accounts/other-entries/${deleteOtherTarget.id}`)
      .then((res) => {
        if (res?.data?.success === false) {
          toast.error(res?.data?.message || res?.data?.errorMessage || "Error deleting entry");
          return;
        }
        setAccountDetails((prev) => prev.filter((item) => item.uuid !== deleteOtherTarget.rowUuid));
        markDirty();
        toast.success("Entry deleted");
        closeDeleteOtherModal();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.errorMessage ||
            err?.response?.data?.message ||
            "Error deleting entry"
        );
      });
  };

  // Delete confirmation
  const deleteData = () => {
    axios
      .delete(`${API_URL}/organization/delete/${openConfirmationModal.uuid}`)
      .then((res) => {
        toast.success(res?.data?.message || "Data deleted successfully");
        setOpenConfirmationModal({ state: false, uuid: null });
        refetchAccountList();
      })
      .catch(() => {
        toast.error("Error deleting data");
      });
  };

  const handleGenerateInvoice = (row) => {
    const computedInvoices =
      row?.module === "Admission" && Array.isArray(row?.admissionDetails)
        ? row.admissionDetails.map((term, idx) => {
            const price = Number(
              term?.dueAmount ??
                term?.totalDueAmount ??
                term?.perSemFee ??
                term?.perSemCost ??
                term?.materialFee ??
                0
            );
            return {
              id: term?.uuid || term?.id || `${row.id}-term-${idx}`,
              description: `Term ${idx + 1}`,
              quantity: 1,
              price,
              gst: "yes",
              total: price,
            };
          })
        : undefined;

    setIsGenerateInvoiceOpen({
      id: row.id,
      state: true,
      data: {
        ...row,
        invoices: computedInvoices,
        invoiceTo: data.clientData?.name,
        address: data.clientData?.address,
        contactNumber: data.clientData?.number,
      },
    });
  };

  // Both open in a new tab so the account being edited stays put. The pages load the admission
  // from the id in the URL, so nothing needs carrying across.
  const openAdmissionPage = (row, path) => {
    const admissionId = row?.admissionValues?.studentDbId;
    if (!admissionId) {
      toast.info("No admission is linked to this account row.");
      return;
    }
    window.open(`/admin/admission/${path}/${admissionId}`, "_blank", "noopener,noreferrer");
  };

  const handleViewAdmission = (row) => openAdmissionPage(row, "edit");

  const handleViewFeeDetails = (row) => openAdmissionPage(row, "fees");

  const clientIdForVerifications = data?.clientData?.id;
  const pendingPaymentsQuery = usePendingPaymentVerifications(
    clientIdForVerifications,
    !!clientIdForVerifications
  );
  const pendingTermsQuery = usePendingTermVerifications(
    clientIdForVerifications,
    !!clientIdForVerifications
  );
  const pendingModulePaymentsQuery = usePendingModulePaymentVerifications(
    clientIdForVerifications,
    !!clientIdForVerifications
  );
  const canReadPaymentVerifications = hasPermissionKey(P.ACCOUNTS.PAYMENT_READ);
  const canVerifyPayments = hasPermissionKey(P.ACCOUNTS.PAYMENT_VERIFY);
  // Account notes hold internal payment/term detail; viewing them needs the Accounts permission.
  const canViewAccountNotes = hasPermissionKey(P.ACCOUNTS.NOTE_READ);
  const openSettlementForRow = useCallback((row) => {
    const admissionId = row?.admissionValues?.studentDbId;
    if (!admissionId) return;
    const values = row.admissionValues || {};
    setSettlementDialog({
      admissionId,
      courseLabel: values.course || values.university || null,
    });
  }, []);

  /**
   * Withdraw and settle from the account itself. An admission is withdrawn from the application
   * form, but the person who then has to decide its money is looking at this page — and a
   * withdrawal notification drops them here.
   */
  const renderAdmissionRowActions = useCallback(
    (row) => {
      const admissionId = row?.admissionValues?.studentDbId;
      if (!admissionId) return null;
      const values = row.admissionValues || {};
      const courseLabel = values.course || values.university || null;

      if (row.admissionStatus === ADMISSION_WITHDRAWN_STATUS) {
        return (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => setSettlementDialog({ admissionId, courseLabel })}
          >
            Withdrawal settlement
          </Button>
        );
      }
      return (
        <ApplicationWithdrawPanel
          inline
          admissionId={admissionId}
          onWithdrawn={() => {
            refetchAccountList();
            refetchStudent?.();
          }}
        />
      );
    },
    [refetchAccountList, refetchStudent]
  );

  const totalPendingVerifications = useMemo(() => {
    const admissionTotal = (accountDetails || [])
      .filter((row) => row.module === "Admission")
      .reduce(
        (sum, row) =>
          sum +
          countPendingVerificationsForAdmission({
            pendingPayments: pendingPaymentsQuery.data ?? [],
            pendingTerms: pendingTermsQuery.data ?? [],
            acountId: row.id,
            studentDbId: row.admissionValues?.studentDbId,
          }),
        0
      );
    const moduleTotal = (pendingModulePaymentsQuery.data ?? []).length;
    return admissionTotal + moduleTotal;
  }, [
    accountDetails,
    pendingPaymentsQuery.data,
    pendingTermsQuery.data,
    pendingModulePaymentsQuery.data,
  ]);

  const openClientVerificationModal = useCallback((scope = null) => {
    setModuleVerificationScope(scope);
    setClientVerificationModalOpen(true);
  }, []);

  const admissionCadIds = useMemo(
    () => (accountDetails || []).filter((r) => r.module === "Admission" && r.id).map((r) => r.id),
    [accountDetails]
  );

  const admissionTermsQueries = useQueries({
    queries: admissionCadIds.map((cadId) => ({
      queryKey: ["admissionTerms", cadId],
      queryFn: async () => {
        const res = await axios.get(`${API_URL}/admission-terms/admission-detail/${cadId}`);
        return res.data?.data ?? [];
      },
      enabled: !!cadId,
      staleTime: 0,
    })),
  });

  const admissionTermsByCadId = useMemo(() => {
    const map = new Map();
    admissionCadIds.forEach((cadId, index) => {
      const query = admissionTermsQueries[index];
      map.set(cadId, {
        data: query?.data ?? null,
        isSuccess: Boolean(query?.isSuccess),
      });
    });
    return map;
  }, [admissionCadIds, admissionTermsQueries]);

  // Hold the editor until master account + dependent lists that reshape overview/admission
  // totals & visibility have settled — avoids flashing stub rows then jumping numbers.
  const admissionTermsPending =
    admissionCadIds.length > 0 &&
    admissionTermsQueries.some((query) => !query?.isFetched && !query?.isError);
  const pendingVerificationsLoading =
    Boolean(clientIdForVerifications) &&
    (pendingPaymentsQuery.isLoading ||
      pendingTermsQuery.isLoading ||
      pendingModulePaymentsQuery.isLoading);
  const isAccountPageLoading =
    accountDataLoading ||
    admissionTermsPending ||
    (Boolean(data?.clientData?.id) && studentAdmissionsLoading) ||
    pendingVerificationsLoading;

  // Admission rows appear once COE is received, any payment is verified, or a payment awaits verification.
  const isAdmissionShown = useCallback(
    (row) =>
      shouldShowAdmissionInAccounts(row, {
        admissionTerms: row.id ? admissionTermsByCadId.get(row.id)?.data : null,
        pendingPayments: pendingPaymentsQuery.data ?? [],
        pendingTerms: pendingTermsQuery.data ?? [],
      }),
    [admissionTermsByCadId, pendingPaymentsQuery.data, pendingTermsQuery.data]
  );

  // Display-only totals for each admission row, priced with its terms' withdrawal and claim-mode
  // fields. Every term counts, imported or not, as the Accounts list and reports count it.
  const projectedAccountDetails = useMemo(
    () =>
      projectAccountDetails(
        accountDetails,
        (row) => (row.id ? admissionTermsByCadId.get(row.id)?.data : null),
        isAdmissionShown
      ),
    [accountDetails, admissionTermsByCadId, isAdmissionShown]
  );

  const getAdmissionCompletionContext = useCallback(
    (row) => {
      if (!row || row.module !== "Admission") {
        return { admissionTerms: null, admissionTermsLoaded: true, pendingVerificationCount: 0 };
      }
      const termsEntry = row.id ? admissionTermsByCadId.get(row.id) : null;
      return {
        admissionTerms: termsEntry?.data ?? null,
        admissionTermsLoaded: row.id ? Boolean(termsEntry?.isSuccess) : true,
        pendingVerificationCount: countPendingVerificationsForAdmission({
          pendingPayments: pendingPaymentsQuery.data ?? [],
          pendingTerms: pendingTermsQuery.data ?? [],
          acountId: row.id,
          studentDbId: row.admissionValues?.studentDbId,
        }),
      };
    },
    [admissionTermsByCadId, pendingPaymentsQuery.data, pendingTermsQuery.data]
  );

  const pendingModuleCadIds = useMemo(() => {
    const ids = new Set();
    (pendingModulePaymentsQuery.data ?? []).forEach((item) => {
      const cadId = item?.clientAccountDetailId ?? item?.payment?.clientAccountDetailId;
      if (cadId != null) ids.add(String(cadId));
    });
    return ids;
  }, [pendingModulePaymentsQuery.data]);

  const visibleEditorRows = useMemo(
    () =>
      projectedAccountDetails
        .filter(isAdmissionShown)
        .map((row) =>
          row.id != null && pendingModuleCadIds.has(String(row.id))
            ? { ...row, hasPendingModulePayment: true }
            : row
        ),
    [projectedAccountDetails, isAdmissionShown, pendingModuleCadIds]
  );

  const tabCounts = useMemo(() => {
    const counts = { overview: visibleEditorRows.length };
    ACCOUNT_TABS.forEach((tab) => {
      if (tab.id !== "overview") counts[tab.id] = 0;
    });
    visibleEditorRows.forEach((row) => {
      const id = tabIdForModule(row.module);
      counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [visibleEditorRows]);

  const tabFilteredRows = useMemo(
    () =>
      activeTabId === "overview"
        ? []
        : visibleEditorRows.filter((row) => tabIdForModule(row.module) === activeTabId),
    [visibleEditorRows, activeTabId]
  );

  const admissionRows = useMemo(
    () => visibleEditorRows.filter((row) => row.module === "Admission"),
    [visibleEditorRows]
  );

  const activeAdmissionDetail = useMemo(() => {
    if (!activeEntryId) return null;
    return (
      admissionRows.find((row) => row.uuid === activeEntryId) ||
      projectedAccountDetails.find(
        (row) => row.uuid === activeEntryId && row.module === "Admission"
      ) ||
      null
    );
  }, [activeEntryId, admissionRows, projectedAccountDetails]);

  // Same merge the term table does, so the totals strip above it agrees with the rows below.
  const activeAdmissionInstallments = useMemo(() => {
    if (!activeAdmissionDetail) return [];
    const terms = activeAdmissionDetail.id
      ? admissionTermsByCadId.get(activeAdmissionDetail.id)?.data
      : null;
    if (!Array.isArray(terms) || terms.length === 0) {
      return activeAdmissionDetail.admissionDetails || [];
    }
    return mergeAdmissionInstallmentsWithTerms(activeAdmissionDetail.admissionDetails, terms);
  }, [activeAdmissionDetail, admissionTermsByCadId]);

  const moduleTotals = useMemo(() => {
    const totals = {};
    ACCOUNT_TABS.forEach((tab) => {
      if (tab.id === "overview") return;
      totals[tab.id] = { due: 0, paid: 0, commission: 0 };
    });
    visibleEditorRows.forEach((row) => {
      const id = tabIdForModule(row.module);
      if (!totals[id]) totals[id] = { due: 0, paid: 0, commission: 0 };
      totals[id].due += Number(row.dueAmount) || 0;
      totals[id].paid += Number(row.paidAmount) || 0;
      totals[id].commission += Number(row.commission) || 0;
    });
    return totals;
  }, [visibleEditorRows]);

  const pendingByModule = useMemo(() => {
    const pending = {};
    ACCOUNT_TABS.forEach((tab) => {
      if (tab.id === "overview") return;
      pending[tab.id] = 0;
    });
    visibleEditorRows.forEach((row) => {
      const id = tabIdForModule(row.module);
      if (row.module === "Admission") {
        const ctx = getAdmissionCompletionContext(row);
        pending[id] = (pending[id] || 0) + (ctx.pendingVerificationCount || 0);
      } else if (row.hasPendingModulePayment) {
        pending[id] = (pending[id] || 0) + 1;
      }
    });
    return pending;
  }, [visibleEditorRows, getAdmissionCompletionContext]);

  const lockedTabModuleConfig = DROPDOWN_LABEL_TO_MODULE_CONFIG[activeTab.moduleValue] || null;

  const deleteAccountEntry = (detail) => handleDeleteInstallment({ original: detail });

  // Others-tab delete: a row that only exists in the editor is dropped locally, but a saved
  // custom entry goes through the entry delete endpoint with a preview — its payments must be
  // checked and purged, which a local removal plus SAVE cannot do.
  const handleDeleteOtherPanelEntry = (detail) => {
    // The entry delete endpoint refuses anything that is not an OTHER entry, so a row that has
    // never been saved (or is not linked yet) is dropped locally instead of 400ing.
    if (!detail?.id || !isOtherEntryRow(detail)) {
      handleDeleteInstallment({ original: detail });
      return;
    }
    if (!hasPermissionKey([P.ACCOUNTS.OTHER_ENTRY_MANAGE, P.ACCOUNTS.DELETE])) {
      toast.error("You are not authorized to delete this entry");
      return;
    }
    setDeleteOtherTarget({ state: true, id: detail.id, rowUuid: detail.uuid });
  };
  const openEyeForDetail = (detail) => handleOpenEyeModal({ original: detail });

  const changeOtherField = (detail, name, value) => {
    setAccountDetails((prev) =>
      prev.map((item) =>
        item.uuid === detail.uuid ? calculateRowValues({ ...item, [name]: value }) : item
      )
    );
    markDirty();
  };
  // Figures modal saves several money/claim fields together so profit is recalculated once.
  const applyOtherFigures = (detail, fields) => {
    setAccountDetails((prev) =>
      prev.map((item) =>
        item.uuid === detail.uuid ? calculateRowValues({ ...item, ...fields }) : item
      )
    );
    markDirty();
  };
  const openInsuranceCommission = (row) => {
    setInsuranceCommissionModal({ open: true, row });
  };
  // The commission ledger is written straight to the server, month by month, so the account has to
  // be re-read rather than patched in place — the row's commission total, profit and completion
  // state are all recomputed there.
  const refreshAfterCommissionChange = () => {
    const masterId = accountDataRef.current?.id;
    if (masterId) getIndividualAccountData(true, masterId);
  };

  // Closing the ledger says no more commission is coming, which is what lets a fully-paid policy
  // read as completed; reopening it is how another month gets recorded.
  const toggleCommissionCompleted = async (row, completed) => {
    if (!row?.id) return;
    setCommissionToggleBusyId(row.id);
    try {
      await setCommissionCompletedRequest(row.id, completed);
      toast.success(completed ? "Commission marked complete" : "Commission reopened");
      refreshAfterCommissionChange();
    } catch (error) {
      toast.error(error?.response?.data?.errorMessage || "Could not update the commission status");
    } finally {
      setCommissionToggleBusyId(null);
    }
  };
  const openLinkedModuleExternal = (row) => {
    const config = LINKED_MODULE_TYPE_TO_CONFIG[row.linkedModuleType];
    if (!config || !row.linkedModuleId) return;
    navigate(`${config.basePath}/edit/${row.linkedModuleId}?tab=payments`);
  };
  const openAdmissionExternal = (row) => {
    const admissionId = row?.admissionValues?.studentDbId;
    if (!admissionId) return;
    navigate(`/admin/admission/edit/${admissionId}`);
  };
  const manageLockedModule = (config) => {
    if (!config || !data?.clientData?.id) return;
    navigate(`${config.basePath}/client/${data.clientData.id}`);
  };

  // Deep-link from notifications: expand admission row or open client verification modal.
  useEffect(() => {
    const focus = state?.accountFocus;
    if (!focus || accountFocusHandledRef.current || accountDataLoading) return;
    if (!accountDetails?.length) return;

    if (focus.openVerificationModal && (focus.moduleType || focus.paymentId)) {
      setClientVerificationModalOpen(true);
      if (focus.moduleType && focus.moduleId != null) {
        setModuleVerificationScope({
          moduleType: focus.moduleType,
          moduleId: focus.moduleId,
        });
      } else {
        setModuleVerificationScope(null);
      }
      accountFocusHandledRef.current = true;
      navigate(pathname, {
        replace: true,
        state: state?.item ? { item: state.item } : undefined,
      });
      return;
    }

    const admissions = accountDetails.filter((d) => d.module === "Admission");
    if (!admissions.length) {
      if (focus.openVerificationModal) {
        setClientVerificationModalOpen(true);
        setModuleVerificationScope(null);
        accountFocusHandledRef.current = true;
        navigate(pathname, {
          replace: true,
          state: state?.item ? { item: state.item } : undefined,
        });
      }
      return;
    }

    const target =
      (focus.admissionId &&
        admissions.find((d) => d.admissionValues?.studentDbId === focus.admissionId)) ||
      admissions[0];

    if (target?.uuid) {
      setActiveTabId("admission");
      setActiveEntryId(target.uuid);
      if (focus.openVerificationModal) {
        setAutoOpenVerification(true);
        setVerificationFocusUuid(target.uuid);
      }
      if (focus.openTermNotes) {
        setTermNotesFocus({ uuid: target.uuid, termNumber: focus.termNumber ?? null });
      }
      if (focus.openWithdrawalSettlement && target.admissionValues?.studentDbId) {
        const values = target.admissionValues || {};
        setSettlementDialog({
          admissionId: values.studentDbId,
          courseLabel: values.course || values.university || null,
        });
      }
    }
    accountFocusHandledRef.current = true;

    navigate(pathname, {
      replace: true,
      state: state?.item ? { item: state.item } : undefined,
    });
  }, [
    state?.accountFocus,
    accountDetails,
    accountDataLoading,
    navigate,
    pathname,
    state?.item,
    setActiveTabId,
    setActiveEntryId,
  ]);

  // Calculate totals for display. Use the projected rows so the Financial Snapshot matches the
  // (full-term) totals now shown on each collapsed Admission row, not just imported installments.
  const totals = calculateTotals(projectedAccountDetails);

  const admissionDetailView = activeAdmissionDetail ? (
    <AccountAdmissionDetailPage
      row={activeAdmissionDetail}
      admissionValues={hydrateAdmissionValues(activeAdmissionDetail.admissionValues || {})}
      clientId={data.clientData?.id}
      clientName={data.clientData?.name}
      onBack={() => setActiveTabId("overview")}
      onRefresh={() => getIndividualAccountData(true, accountData?.id)}
      onOpenEye={openEyeForDetail}
      onViewAdmission={handleViewAdmission}
      onViewFeeDetails={handleViewFeeDetails}
      onGenerateInvoice={handleGenerateInvoice}
      onToggleNotes={canViewAccountNotes ? handleToggleNotes : undefined}
      accountDetails={accountDetails}
      setAccountDetails={setAccountDetails}
      clientData={data.clientData}
      onDirty={markDirty}
      autoOpenVerification={autoOpenVerification}
      verificationFocusUuid={verificationFocusUuid}
      onVerificationOpened={() => {
        if (verificationFocusUuid) {
          setVerificationFocusUuid(null);
          setAutoOpenVerification(false);
        }
      }}
      autoCloseVerificationWhenEmpty={autoOpenVerification}
      termNotesFocus={termNotesFocus}
      onTermNotesOpened={() => {
        if (termNotesFocus) setTermNotesFocus(null);
      }}
      onVerifySuccess={() => {
        const masterId = accountDataRef.current?.id;
        if (masterId) getIndividualAccountData(true, masterId);
        refetchStudent?.();
      }}
      autoSaveStatus={autoSaveStatus}
      onSave={handleSubmit}
      isSaving={isSaving || isPending}
      onAdjustSettlement={openSettlementForRow}
      totalsInstallments={activeAdmissionInstallments}
    />
  ) : null;

  return (
    <>
      {isAccountPageLoading ? (
        <div className="account-details-table flex flex-wrap mt-4 dashBody">
          <div className="w-full mb-2 px-4">
            <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-app-surface text-app-text min-h-[60vh] items-center justify-center gap-3 px-4 py-16">
              <CircularProgress size={36} />
              <p className="text-sm text-app-text-muted m-0">Loading account…</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {admissionDetailView}
          {!activeAdmissionDetail && (
            <div className="account-details-table flex flex-wrap mt-4 dashBody">
              <div className="w-full mb-2 px-4">
                <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded h-full bg-app-surface text-app-text">
                  <AccountEditorHeader
                    clientId={data.clientData?.id}
                    clientName={data.clientData?.name}
                    onBack={() => navigate(-1)}
                    onRefresh={() => getIndividualAccountData(true, accountData?.id)}
                    autoSaveStatus={autoSaveStatus}
                    pendingCount={totalPendingVerifications}
                    canReadVerifications={canReadPaymentVerifications}
                    onOpenVerifications={() => openClientVerificationModal(null)}
                    onSave={handleSubmit}
                    isSaving={isSaving || isPending}
                    showSendToProvider={Boolean(accountData?.id)}
                    onSendToProvider={() => setSendToProviderOpen(true)}
                  />
                  <div className="px-4 py-4 space-y-3">
                    <AccountFinancialSummary totals={totals} />
                    <Tabs
                      value={activeTabId}
                      onChange={(e, val) => setActiveTabId(val)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={getThemedTabsSx({
                        minHeight: 40,
                        mb: 0,
                        borderRadius: "12px",
                        "& .MuiTab-root": { minHeight: 40, fontWeight: 600, py: 0.75 },
                      })}
                    >
                      {ACCOUNT_TABS.map((tab) => (
                        <Tab
                          key={tab.id}
                          value={tab.id}
                          disableRipple
                          label={
                            <span className="flex items-center gap-1.5">
                              {tab.label}
                              {tab.id !== "overview" && (
                                <span
                                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                                    pendingByModule[tab.id]
                                      ? "bg-amber-100 text-amber-800"
                                      : "bg-app-surface-muted text-app-text-secondary"
                                  }`}
                                >
                                  {tabCounts[tab.id] || 0}
                                </span>
                              )}
                            </span>
                          }
                        />
                      ))}
                    </Tabs>
                    <div className="rounded-xl border border-app-border bg-app-surface p-4 shadow-sm">
                      {activeTabId === "overview" && (
                        <AccountOverviewPanel
                          tabCounts={tabCounts}
                          moduleTotals={moduleTotals}
                          pendingByModule={pendingByModule}
                          onSelectTab={setActiveTabId}
                        />
                      )}
                      {activeTabId === "admission" && (
                        <AccountAdmissionPanel
                          rows={admissionRows}
                          onSelectEntry={setActiveEntryId}
                          onAddEntry={handleAddAdmissionEntry}
                          onDelete={deleteAccountEntry}
                          onOpenExternal={openAdmissionExternal}
                          onToggleNotes={canViewAccountNotes ? handleToggleNotes : undefined}
                          onOpenCourseDetails={openEyeForDetail}
                          onOpenAdmission={handleViewAdmission}
                          onViewAdmission={handleViewFeeDetails}
                          getCompletionContext={getAdmissionCompletionContext}
                          isCompleted={isAdmissionRowCompleted}
                          hydrateAdmissionValues={hydrateAdmissionValues}
                          renderRowActions={renderAdmissionRowActions}
                        />
                      )}
                      {["visa", "insurance", "skill", "rpl"].includes(activeTabId) && (
                        <AccountLinkedModulePanel
                          moduleLabel={activeTab.label}
                          rows={tabFilteredRows}
                          moduleConfig={lockedTabModuleConfig}
                          clientId={data?.clientData?.id}
                          onManageModule={manageLockedModule}
                          onOpenPayments={openModulePaymentsModal}
                          onOpenCharges={(row) => setChargesModal({ open: true, row })}
                          onOpenCommission={
                            activeTabId === "insurance" ? openInsuranceCommission : undefined
                          }
                          onOpenNotes={canViewAccountNotes ? handleToggleNotes : undefined}
                          onOpenExternal={openLinkedModuleExternal}
                          onOpenPendingVerifications={(row) =>
                            openClientVerificationModal({
                              moduleType: row.linkedModuleType,
                              moduleId: row.linkedModuleId,
                            })
                          }
                          onToggleCommissionCompleted={
                            activeTabId === "insurance" ? toggleCommissionCompleted : undefined
                          }
                          commissionToggleBusyId={commissionToggleBusyId}
                          canReadVerifications={canReadPaymentVerifications}
                        />
                      )}
                      {activeTabId === "others" && (
                        <AccountOthersPanel
                          rows={tabFilteredRows}
                          onAddEntry={addAccountDetails}
                          onChangeField={changeOtherField}
                          onApplyFigures={applyOtherFigures}
                          onOpenPayments={openModulePaymentsModal}
                          onOpenNotes={canViewAccountNotes ? handleToggleNotes : undefined}
                          onOpenPendingVerifications={(row) =>
                            openClientVerificationModal({
                              moduleType: row.linkedModuleType,
                              moduleId: row.linkedModuleId,
                            })
                          }
                          canReadVerifications={canReadPaymentVerifications}
                          onDelete={handleDeleteOtherPanelEntry}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="Course"
          handleCancel={() => setOpenConfirmationModal({ state: false, uuid: null })}
          handleDelete={deleteData}
        />
      )}
      {deleteOtherTarget.state && (
        <DeleteModal
          open={deleteOtherTarget.state}
          item="custom entry"
          handleCancel={closeDeleteOtherModal}
          handleDelete={handleDeleteOtherEntry}
          preview={deleteOtherPreviewQuery.data ?? null}
          previewLoading={deleteOtherPreviewQuery.isFetching}
        />
      )}
      {openEyeModal.state && (
        <EyeModal
          open={openEyeModal}
          setOpen={setOpenEyeModal}
          values={studentValues}
          setValues={setStudentValues}
          handleSubmit={handleSubmitSideDrawer}
          admissionDetailId={openEyeModal.id}
          lockedInstallments={
            accountDetails.find((item) => item.uuid === openEyeModal.uuid)?.admissionDetails || []
          }
        />
      )}
      <Dialog
        open={admissionSelectDialog.open}
        onClose={() =>
          setAdmissionSelectDialog({
            open: false,
            rowUuid: null,
            options: [],
            missingScheduleCount: 0,
          })
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select admission</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which admission to use for this account entry. Only admissions with a term
            schedule in Offer letter received are listed.
          </Typography>
          {admissionSelectDialog.missingScheduleCount > 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              {admissionSelectDialog.missingScheduleCount} other admission(s) are not listed because
              they have no term schedule yet.
            </Typography>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {admissionSelectDialog.options.map((student) => {
              const appData = parseAdmissionApplicationData(student.applicationData);
              const termCount = Array.isArray(appData?.termSchedule)
                ? appData.termSchedule.length
                : 0;
              const course =
                appData?.courseName ||
                courseList?.find((c) => c.id === student.courseId)?.name ||
                student.name ||
                `Admission #${student.id}`;
              const college =
                appData?.universityName ||
                uniData?.find((u) => u.id === student.universityId)?.name ||
                "";
              return (
                <button
                  key={student.id}
                  type="button"
                  className="text-left rounded-xl border border-app-border bg-app-surface p-4 hover:border-primary/50 hover:shadow-md transition-all"
                  onClick={() => {
                    const applied = applyAdmissionToRow(admissionSelectDialog.rowUuid, student);
                    if (!applied) return;
                    setActiveTabId("admission");
                    setActiveEntryId(admissionSelectDialog.rowUuid);
                    setAdmissionSelectDialog({
                      open: false,
                      rowUuid: null,
                      options: [],
                      missingScheduleCount: 0,
                    });
                  }}
                >
                  <p className="text-sm font-semibold text-app-text m-0 truncate">{course}</p>
                  <p className="text-xs text-app-text-muted m-0 mt-1 truncate">
                    {[college, student.intake, student.status].filter(Boolean).join(" · ") ||
                      "Admission"}
                  </p>
                  <p className="text-xs font-medium text-app-text-secondary m-0 mt-2">
                    {termCount} term{termCount === 1 ? "" : "s"}
                  </p>
                </button>
              );
            })}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setAdmissionSelectDialog({
                open: false,
                rowUuid: null,
                options: [],
                missingScheduleCount: 0,
              })
            }
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {sendToProviderOpen && (
        <SendToProviderModal
          open={sendToProviderOpen}
          onClose={() => setSendToProviderOpen(false)}
          accountMasterId={accountData?.id}
          groups={[]}
          accountDetails={accountDetails}
          onSent={() => {
            setSendToProviderOpen(false);
          }}
        />
      )}
      {openNotesModal.state && canViewAccountNotes && (
        <NotesModal
          open={openNotesModal.state}
          handleClose={handleClose}
          data={openNotesModal.data}
          accountDetails={accountDetails}
          setAccountDetails={setAccountDetails}
          subtable={false}
          onLocalNoteAdded={markDirty}
          onPaymentNoteDeleted={async (note) => {
            const cadId = openNotesModal.data?.id;
            const module = openNotesModal.data?.module;
            if (!cadId || module === "Admission" || !isPaymentNote(note)) return;

            const payments = modulePaymentsMap[cadId] || [];
            const payment = findPaymentForNote(note, payments);
            if (!payment) return;

            const next = payments.filter((p) => p.id !== payment.id);
            setModulePaymentsMap((prev) => ({ ...prev, [cadId]: next }));
            const totalPaid = next.reduce((s, p) => s + (Number(p.amount) || 0), 0);
            setAccountDetails((prev) =>
              prev.map((r) => (r.id === cadId ? { ...r, paidAmount: String(totalPaid) } : r))
            );

            await syncCadPayments(
              cadId,
              next.map((p) => ({
                uuid: p.id,
                amount: p.amount,
                date: p.date,
                reference: p.reference,
                createdAt: p.createdAt,
              }))
            );
            markDirty();
          }}
        />
      )}
      {isGenerateInvoiceOpen.state && (
        <GenerateInvoice
          open={isGenerateInvoiceOpen}
          onClose={() => setIsGenerateInvoiceOpen({ id: null, state: false, data: null })}
          rowData={isGenerateInvoiceOpen.data}
        />
      )}
      {clientVerificationModalOpen && (
        <AccountVerificationModal
          open={clientVerificationModalOpen}
          onClose={() => {
            setClientVerificationModalOpen(false);
            setModuleVerificationScope(null);
          }}
          clientId={data?.clientData?.id}
          canAct={canVerifyPayments}
          autoCloseWhenEmpty={Boolean(moduleVerificationScope)}
          scopeModuleType={moduleVerificationScope?.moduleType ?? null}
          scopeModuleId={moduleVerificationScope?.moduleId ?? null}
          onActionComplete={() => {
            const masterId = accountDataRef.current?.id;
            if (masterId) getIndividualAccountData(true, masterId);
            pendingModulePaymentsQuery.refetch();
            pendingPaymentsQuery.refetch();
            pendingTermsQuery.refetch();
          }}
        />
      )}
      {paymentsModal.open && paymentsModal.row && isLinkedModuleRow(paymentsModal.row) && (
        <LinkedModulePaymentsModal
          open={paymentsModal.open}
          onClose={() => setPaymentsModal({ open: false, row: null })}
          cadId={paymentsModal.row.id}
          moduleName={paymentsModal.row.module || ""}
          agreedAmount={Number(paymentsModal.row.agreedAmount) || 0}
          discountAmount={Number(paymentsModal.row.discount) || 0}
          isOtherEntry={isOtherEntryRow(paymentsModal.row)}
          onPaymentsChanged={() => {
            const row = paymentsModal.row;
            if (!row?.id) return;
            hydrateModulePaymentsForRow(row, setModulePaymentsMap, setAccountDetails);
            const masterId = accountDataRef.current?.id;
            if (masterId) getIndividualAccountData(true, masterId);
            pendingModulePaymentsQuery.refetch();
          }}
        />
      )}
      {paymentsModal.open && paymentsModal.row && !isLinkedModuleRow(paymentsModal.row) && (
        <GenericPaymentsModal
          open={paymentsModal.open}
          onClose={() => setPaymentsModal({ open: false, row: null })}
          moduleName={paymentsModal.row.module || ""}
          cadId={paymentsModal.row.id || null}
          agreedAmount={Number(paymentsModal.row.agreedAmount) || 0}
          discountAmount={Number(paymentsModal.row.discount) || 0}
          readOnly={!String(paymentsModal.row.module || "").trim()}
          readOnlyReason="Name this entry and save it first. A custom entry's payments go through account verification."
          clientId={data.clientData?.id ?? null}
          clientName={data.clientData?.name ?? ""}
          payments={modulePaymentsMap[paymentsModal.row.id] || []}
          onChangePayments={(newPayments) => {
            const cadId = paymentsModal.row.id;
            setModulePaymentsMap((prev) => ({ ...prev, [cadId]: newPayments }));
            const totalPaid = newPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
            setAccountDetails((prev) =>
              prev.map((r) => (r.id === cadId ? { ...r, paidAmount: String(totalPaid) } : r))
            );
          }}
          onPaymentsSynced={(synced) => {
            const cadId = paymentsModal.row.id;
            if (Array.isArray(synced)) {
              const rowNotes = accountDetails.find((r) => r.id === cadId)?.notes || [];
              const normalized = enrichPaymentsFromNotes(normalizeModulePayments(synced), rowNotes);
              setModulePaymentsMap((prev) => ({ ...prev, [cadId]: normalized }));
              const totalPaid = normalized.reduce((s, p) => s + (Number(p.amount) || 0), 0);
              setAccountDetails((prev) =>
                prev.map((r) => (r.id === cadId ? { ...r, paidAmount: String(totalPaid) } : r))
              );
            }
          }}
          onAppendRowNote={async (note) => {
            const rowUuid = paymentsModal.row?.uuid;
            const cadId = paymentsModal.row?.id;
            if (!rowUuid) return;
            const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
            let savedNote = {
              ...note,
              createdBy: userDetail?.name ?? userDetail?.firstName ?? "User",
            };
            if (cadId && note.text) {
              try {
                const apiNote = await persistAccountDetailNote(cadId, note.text);
                if (apiNote) {
                  savedNote = {
                    ...savedNote,
                    id: apiNote.id,
                    uuid: apiNote.uuid ?? savedNote.uuid,
                    text: apiNote.text ?? apiNote.noteText ?? savedNote.text,
                  };
                }
              } catch (err) {
                // Rethrow only — the payments modal owns the single message for this outcome, and
                // toasting here too put a failure alongside its own success.
                throw err;
              }
            }
            setAccountDetails((prev) =>
              prev.map((r) => {
                if (r.uuid !== rowUuid) return r;
                return {
                  ...r,
                  notes: [...(r.notes || []), savedNote],
                  notesCount: (r.notesCount || 0) + 1,
                };
              })
            );
            markDirty();
          }}
          onRemoveRowNote={async (payment) => {
            const rowUuid = paymentsModal.row?.uuid;
            if (!rowUuid || !payment) return;
            const row = accountDetailsRef.current?.find((r) => r.uuid === rowUuid);
            const note = findNoteForPayment(payment, row?.notes || []);
            try {
              if (note?.id) {
                await axios.delete(`${API_URL}/notes/${note.id}?parentType=ACCOUNT_DETAIL`);
              }
              if (note) {
                setAccountDetails((prev) =>
                  prev.map((r) => {
                    if (r.uuid !== rowUuid) return r;
                    const remaining = (r.notes || []).filter((n) => !isSameNote(n, note));
                    return {
                      ...r,
                      notes: remaining,
                      notesCount: Math.max(remaining.length, 0),
                    };
                  })
                );
                markDirty();
              }
            } catch (err) {
              toast.error(err?.response?.data?.errorMessage || "Failed to delete linked note.");
              throw err;
            }
          }}
          rowNotes={accountDetails.find((r) => r.uuid === paymentsModal.row?.uuid)?.notes || []}
        />
      )}
      <WithdrawalSettlementDialog
        open={Boolean(settlementDialog)}
        admissionId={settlementDialog?.admissionId}
        courseLabel={settlementDialog?.courseLabel}
        onClose={() => setSettlementDialog(null)}
        onSaved={() => {
          refetchAccountList();
          refetchStudent?.();
        }}
      />

      <EditModuleChargesModal
        open={chargesModal.open}
        onClose={() => setChargesModal({ open: false, row: null })}
        accountRow={
          accountDetails.find((r) => r.uuid === chargesModal.row?.uuid) || chargesModal.row
        }
        onSaved={(fields) => {
          const uuid = chargesModal.row?.uuid;
          if (!uuid) return;

          optimisticRollbackRef.current = accountDetails;
          const nextDetails = accountDetails.map((r) =>
            r.uuid === uuid
              ? calculateRowValues({
                  ...r,
                  cost: fields.cost,
                  discount: fields.discount,
                  agreed: fields.agreed,
                  referral: fields.referral,
                })
              : r
          );
          setAccountDetails(nextDetails);

          if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
          }

          const d = dataRef.current || data;
          const accD = accountDataRef.current || accountData;
          if (!d?.clientData?.id) {
            markDirty();
            return;
          }

          const totals = calculateTotals(nextDetails);
          const referralEmail = usersList?.find(
            (user) => user.name === clientData?.referral || user?.email === clientData?.referral
          )?.email;
          const payload = {
            id: accD?.id,
            uuid: d.uuid,
            clientId: d.clientData?.id,
            caseOfficer: d.caseOfficer,
            referral: referralEmail,
            totalAmount: totals.totalPaid,
            agentCost: totals.totalCost,
            dueAmount: totals.totalDue,
            profitLoss: totals.totalProfitLoss,
            accountDetails: nextDetails.map(mapAccountDetailForPayload),
          };

          pendingSuccessToastRef.current = "Charges updated successfully";
          isManualSavingRef.current = true;
          setIsSaving(true);
          mutate(payload);
        }}
      />
      {insuranceCommissionModal.open && (
        <InsuranceCommissionModal
          open={insuranceCommissionModal.open}
          onClose={() => setInsuranceCommissionModal({ open: false, row: null })}
          row={
            accountDetails.find((r) => r.uuid === insuranceCommissionModal.row?.uuid) ||
            insuranceCommissionModal.row
          }
          onAccountChanged={refreshAfterCommissionChange}
        />
      )}
    </>
  );
};

export default AddAccounts;
